import React, { useEffect, useState } from 'react';
import DataTable from '../../components/tables/DataTable';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Toast from '../../components/common/Toast';
import { useToast } from '../../hooks/useToast';
import siteService from '../../services/siteService';

import AddSiteModal from '../../components/features/sites/AddSiteModal';
import EditSiteModal from '../../components/features/sites/EditSiteModal';

const SitesView = () => {
  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);

  const { toasts, removeToast, success, error } = useToast();

  useEffect(() => {
    loadSites();
  }, []);

  useEffect(() => {
    filterSites();
  }, [searchTerm, sites]);

  const loadSites = async () => {
    setLoading(true);
    const result = await siteService.getAllSites();
    if (result.success) {
      setSites(result.data);
      setFilteredSites(result.data);
    } else {
      error(result.error || 'Failed to load Sites');
    }
    setLoading(false);
  };

  const filterSites = () => {
    let filtered = [...sites];

    if (searchTerm) {
      filtered = filtered.filter(site =>
        site.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (site.company?.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        site.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredSites(filtered);
  };

  const handleAddSite = async (siteData) => {
    const result = await siteService.createSite(siteData);
    if (result.success) {
      success('Site added successfully!');
      await loadSites();
    } else {
      error(result.error || 'Failed to add Site');
    }
  };

  const handleEditSite = (site) => {
    setSelectedSite(site);
    setShowEditModal(true);
  };

  const handleUpdateSite = async (siteId, siteData) => {
    const result = await siteService.updateSite(siteId, siteData);
    if (result.success) {
      success('Site updated successfully!');
      await loadSites();
    } else {
      error(result.error || 'Failed to update Site');
    }
  };

  const handleDeleteSite = (site) => {
    setSelectedSite(site);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    const result = await siteService.deleteSite(selectedSite.id);
    if (result.success) {
      success('Site deleted successfully!');
      await loadSites();
    } else {
      error(result.error || 'Failed to delete Site');
    }
    setShowDeleteDialog(false);
    setSelectedSite(null);
  };

  const handleExport = async () => {
    const result = await siteService.exportSitesCSV();
    if (result.success) {
      success('Sites exported successfully!');
    } else {
      error(result.error || 'Failed to export Sites');
    }
  };

  const filterBarStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between'
  };

  const columns = [
    {
      key: 'name_location',
      label: 'Site & Location',
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{row.name}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{row.location}</div>
        </div>
      )
    },
    {
      key: 'company',
      label: 'Company',
      render: (value, row) => (
        <span style={{ fontWeight: '500' }}>
          {row.company?.company_name || row.company_name || 'Unassigned'}
        </span>
      )
    },
    {
      key: 'address',
      label: 'Address',
      render: (value) => (
        <div style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '13px' }}>
          {value || 'N/A'}
        </div>
      )
    },
    {
      key: 'spoc',
      label: 'SPOC',
      render: (value) => value?.name || <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '13px' }}>Not Assigned</span>
    },
    {
      key: 'team_lead',
      label: 'Team Lead Assigned',
      render: (value) => value?.name || <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '13px' }}>Not Assigned</span>
    },
    {
      key: 'po_number',
      label: 'PO Number',
      render: (value) => value || <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '13px' }}>No Active Contract</span>
    },
    {
      key: 'active_tickets',
      label: 'Tickets',
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '700',
          background: 'var(--surface-light)',
          color: 'var(--text-primary)'
        }}>
          {value || 0} Active
        </span>
      )
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
        Loading Sites...
      </div>
    );
  }

  // Build the deletion warning dynamically
  const dependencies = [];
  if (selectedSite?.spocs_count > 0) dependencies.push(`${selectedSite.spocs_count} SPOC(s)`);
  if (selectedSite?.team_leads_count > 0) dependencies.push(`${selectedSite.team_leads_count} Team Lead(s)`);
  if (selectedSite?.active_contracts > 0) dependencies.push(`${selectedSite.active_contracts} Contract(s)`);
  
  const deleteMessage = dependencies.length > 0
    ? `Warning: Site "${selectedSite?.name}" currently has ${dependencies.join(', ')} attached to it. Please reassign or remove them before deleting this site.`
    : `Are you sure you want to delete the site "${selectedSite?.name}"? This action cannot be undone.`;

  return (
    <div>
      {toasts.map(toast => (
        <Toast key={toast.id} message={toast.message} type={toast.type} onClose={() => removeToast(toast.id)} />
      ))}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
          Manage Sites
        </h2>
        <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-secondary)' }}>
          Total Sites: <span style={{ color: 'var(--primary)' }}>{sites.length}</span>
        </div>
      </div>

      <div style={filterBarStyle}>
        <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
          <Input
            placeholder="Search sites by name, location or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            }
          />
        </div>
        
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button variant="secondary" onClick={handleExport}>
            Export CSV
          </Button>
          <Button variant="secondary" onClick={loadSites}>
            Refresh
          </Button>
          <Button variant="primary" onClick={() => setShowAddModal(true)}>
            Add Site
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredSites}
        actions={[
          { label: ' Edit', onClick: handleEditSite },
          { label: ' Delete', onClick: handleDeleteSite }
        ]}
        pageSize={15}
      />

      {/* Modals */}
      <AddSiteModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddSite}
      />

      <EditSiteModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedSite(null);
        }}
        onSubmit={handleUpdateSite}
        site={selectedSite}
      />

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedSite(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Site"
        message={deleteMessage}
        confirmText="Delete"
        type="danger"
        // If there are dependencies, you can visually disable the dialog or handle it purely backend-side. 
        // We handle it in the service by returning success: false, but UI-wise a warning is good.
      />
    </div>
  );
};

export default SitesView;